import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaHistory,
  FaExclamationTriangle,
  FaChartLine,
  FaWrench,
  FaListAlt,
  FaInfoCircle,
  FaSyncAlt,
} from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  getStatusLabel,
  getHealthColor,
  SMPS_COLORS,
} from '../utils/smpsConstants';
import { fetchDeviceHistory, DeviceHistoryResponse } from '../utils/smpsApiService';
import '../smps-dashboard.scss';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

type HistoryTab = 'voltage' | 'maintenance' | 'events';

const SMPSDeviceHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<HistoryTab>('voltage');

  // API data state
  const [historyData, setHistoryData] = useState<DeviceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load data function using conventional axios method
  const loadData = useCallback(async (showLoading = true) => {
    if (!id) return;

    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setIsError(false);

    try {
      const data = await fetchDeviceHistory({ deviceId: id, size: 100 });
      setHistoryData(data);
    } catch (err) {
      console.error('Error loading device history:', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadData(false);
      }
    }, 60000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadData, isLoading, isRefreshing]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    loadData(false);
  }, [loadData]);

  const deviceData = historyData?.device || null;
  const voltageHistory = historyData?.voltageHistory || [];
  const events = historyData?.events || [];
  // Maintenance records not available from API yet
  const maintenanceHistory: any[] = [];

  // Voltage trend chart data
  const voltageChartData = useMemo(() => ({
    labels: voltageHistory.map((h) => h.timestamp.split(' ')[1]),
    datasets: [
      {
        label: 'DC Voltage (V)',
        data: voltageHistory.map((h) => h.dcVoltage),
        borderColor: SMPS_COLORS.primary,
        backgroundColor: 'rgba(26, 82, 118, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Battery Voltage (V)',
        data: voltageHistory.map((h) => h.batteryVoltage),
        borderColor: SMPS_COLORS.statusWarning,
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        tension: 0.4,
      },
    ],
  }), [voltageHistory]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 40,
        max: 56,
      },
    },
  };

  const tabs: { id: HistoryTab; label: string; icon: React.ReactNode }[] = [
    { id: 'voltage', label: 'Voltage History', icon: <FaChartLine /> },
    { id: 'maintenance', label: 'Maintenance', icon: <FaWrench /> },
    { id: 'events', label: 'Events Log', icon: <FaListAlt /> },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="smps-dashboard">
        <div className="detail-page">
          <div className="no-data">
            <FaSyncAlt className="no-data-icon spin" />
            <h3>Loading device history...</h3>
          </div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (isError || !deviceData) {
    return (
      <div className="smps-dashboard">
        <div className="detail-page">
          <div className="no-data">
            <FaExclamationTriangle className="no-data-icon" />
            <h3>{isError ? 'Failed to load device history' : 'Device not found'}</h3>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              {isError ? 'Please check your connection and try again' : 'The requested device could not be found'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {isError && (
                <button className="view-btn" onClick={() => refetch()}>
                  <FaSyncAlt /> Retry
                </button>
              )}
              <button className="back-btn" onClick={() => navigate('/smps-dashboard')}>
                <FaArrowLeft /> Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="smps-dashboard">
      <div className="detail-page">
        {/* Header */}
        <div className="detail-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/smps-dashboard')}>
              <FaArrowLeft />
            </button>
            <div className="header-info">
              <h1>
                <FaHistory />
                {deviceData.name} - History
              </h1>
              <div className="header-meta">
                <span className="station-badge">{deviceData.station}</span>
                <span
                  className="health-badge"
                  style={{ backgroundColor: getHealthColor(deviceData.status) }}
                >
                  {getStatusLabel(deviceData.status)}
                </span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="view-btn"
              onClick={() => refetch()}
              disabled={isRefreshing}
              style={{ opacity: isRefreshing ? 0.7 : 1 }}
            >
              <FaSyncAlt className={isRefreshing ? 'spin' : ''} /> {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="view-btn" onClick={() => navigate(`/smps-device-detail?deviceId=${id}&deviceName=${deviceData.name}`)}>
              <FaInfoCircle /> View Details
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="detail-content">
          {/* Voltage History Tab */}
          {activeTab === 'voltage' && (
            <div className="history-section">
              <div className="chart-card full-width">
                <div className="chart-title">
                  <h3><FaChartLine /> Voltage Trend</h3>
                </div>
                <div className="chart-container" style={{ height: '300px' }}>
                  <Line data={voltageChartData} options={chartOptions} />
                </div>
              </div>

              <div className="table-container">
                <div className="table-header">
                  <div className="table-title">Voltage Records</div>
                  <div className="table-count">Showing {voltageHistory.length} records</div>
                </div>
                <div className="table-scroll">
                  <table className="devices-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>DC Voltage</th>
                        <th>Load Current</th>
                        <th>Battery Voltage</th>
                        <th>Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voltageHistory.map((record) => (
                        <tr key={record.id}>
                          <td>{record.timestamp}</td>
                          <td>{record.dcVoltage}V</td>
                          <td>{record.loadCurrent}A</td>
                          <td>{record.batteryVoltage}V</td>
                          <td>{record.efficiency}%</td>
                        </tr>
                      ))}
                      {voltageHistory.length === 0 && (
                        <tr>
                          <td colSpan={5} className="no-data-cell">No voltage history available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === 'maintenance' && (
            <div className="table-container">
              <div className="table-header">
                <div className="table-title">Maintenance Records</div>
                <div className="table-count">Showing {maintenanceHistory.length} records</div>
              </div>
              <div className="table-scroll">
                <table className="devices-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Performed By</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceHistory.map((record) => (
                      <tr key={record.id}>
                        <td>{record.date}</td>
                        <td>
                          <span className={`type-badge ${record.type.toLowerCase()}`}>
                            {record.type}
                          </span>
                        </td>
                        <td>{record.description}</td>
                        <td>{record.performedBy}</td>
                        <td>
                          <span className={`status-badge ${record.status.toLowerCase().replace(' ', '-')}`}>
                            {record.status}
                          </span>
                        </td>
                        <td>{record.notes || '-'}</td>
                      </tr>
                    ))}
                    {maintenanceHistory.length === 0 && (
                      <tr>
                        <td colSpan={6} className="no-data-cell">No maintenance records available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Events Log Tab */}
          {activeTab === 'events' && (
            <div className="table-container">
              <div className="table-header">
                <div className="table-title">Events Log</div>
                <div className="table-count">Showing {events.length} events</div>
              </div>
              <div className="table-scroll">
                <table className="devices-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Event Type</th>
                      <th>Description</th>
                      <th>Severity</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id}>
                        <td>{event.timestamp}</td>
                        <td>
                          <span className={`type-badge ${event.eventType}`}>
                            {event.eventType.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{event.description}</td>
                        <td>
                          <span className={`severity-badge ${event.severity}`}>
                            {event.severity}
                          </span>
                        </td>
                        <td>{event.user || '-'}</td>
                      </tr>
                    ))}
                    {events.length === 0 && (
                      <tr>
                        <td colSpan={5} className="no-data-cell">No events logged</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMPSDeviceHistory;
